import { error, success } from "@redux-requests/core";
import { areYearsSimilar } from "@utils/helpers";
import { getFilteredInteractions, getInteractionsRelatedItems } from "features/analytics/analyticsHelper";
import { getSelectableIds } from "utils/helpers";

const initialState = {
	originalData: {},
	allFilterOptions: {},
	selectedFilterOptions: {},
	currentData: {},
	savedAnalyticFilters: {},
	loading: false,
};

const vocAnalysisReducer = (state = initialState, action) => {
	const responseData = action?.response?.data;

	switch (action.type) {
		case success("GET_VOC_ANALYSIS_DATA"): {
			const { isExportMode } = action?.meta?.requestAction?.params;

			if (isExportMode) {
				const { cjms, personas, touchpoints, metrics, contactInteractions, personaInteractions, date } = state?.savedAnalyticFilters;
				const filteredCjms = responseData?.cjms.filter((c) => cjms?.includes(c?.id));
				const filteredPersonas = responseData?.personas.filter((p) => personas?.includes(p?.id));
				const filteredTouchpoints = responseData?.touchpoints.filter((tp) => touchpoints?.includes(tp?.id));
				const filteredMetrics = responseData?.metrics.filter((m) => metrics?.includes(m?.id));
				const filteredPersonaInteractions = responseData?.personaInteractions.filter((pi) => personaInteractions?.includes(pi?.id));
				const filteredContactInteractions = responseData?.contactInteractions.filter((ci) => contactInteractions?.includes(ci?.id));

				const filters = {
					cjms: filteredCjms,
					personas: filteredPersonas,
					touchpoints: filteredTouchpoints,
					metrics: filteredMetrics,
					date: date,
				};
				return {
					...state,
					originalData: responseData,
					allFilterOptions: { ...filters },
					selectedFilterOptions: { ...filters },
					currentData: {
						personaInteractions: filteredPersonaInteractions,
						contactInteractions: filteredContactInteractions,
					},
					loading: false,
				};
			}

			const date = new Date();
			const { cjms, personas, contactInteractions, personaInteractions } = responseData;

			const currentContactInteractions = contactInteractions?.filter((interaction) => areYearsSimilar(interaction?.date, date));
			const { touchpoints, metrics } = getInteractionsRelatedItems({
				interactions: [...personaInteractions, ...currentContactInteractions],
				allTouchpoints: responseData?.touchpoints,
				allMetrics: responseData?.metrics,
			});

			const filters = {
				cjms,
				personas,
				touchpoints,
				metrics,
				date: new Date(),
			};
			return {
				...state,
				originalData: responseData,
				allFilterOptions: { ...filters },
				selectedFilterOptions: { ...filters },
				currentData: {
					personaInteractions: personaInteractions,
					contactInteractions: currentContactInteractions,
				},
				loading: false,
			};
		}
		case error("GET_DASHBOARD_DATA"):
			return state;
		case "APPLY_FILTER_VOC_ANALYSIS": {
            const { dataAttribute, selectedItems } = action?.payload;

			switch (dataAttribute) {
				case "cjms": {
					const selectablePersonaIds = getSelectableIds({
						selectedItems,
						linkedAttribute: "personaIds",
					});

					const filteredPersonas = state.originalData.personas?.filter((persona) => selectablePersonaIds.includes(persona.id));
					const { currentPersonaInteractions, currentContactInteractions } = getFilteredInteractions({
						personaInteractions: state?.originalData?.personaInteractions,
						contactInteractions: state?.originalData?.contactInteractions,
						cjms: selectedItems,
						personas: filteredPersonas,
						date: state?.selectedFilterOptions?.date,
					});
					const { touchpoints, metrics } = getInteractionsRelatedItems({
						interactions: [...currentPersonaInteractions, ...currentContactInteractions],
						allTouchpoints: state?.originalData?.touchpoints,
						allMetrics: state?.originalData?.metrics,
					});

					return {
						...state,
						allFilterOptions: { ...state?.allFilterOptions, personas: filteredPersonas, touchpoints, metrics },
						selectedFilterOptions: {
							...state?.selectedFilterOptions,
							cjms: selectedItems,
							personas: filteredPersonas,
							touchpoints,
							metrics,
						},
						currentData: {
							personaInteractions: currentPersonaInteractions,
							contactInteractions: currentContactInteractions,
						},
						loading: false,
					};
				}
				case "personas": {
					const { currentPersonaInteractions, currentContactInteractions } = getFilteredInteractions({
						personaInteractions: state?.originalData?.personaInteractions,
						contactInteractions: state?.originalData?.contactInteractions,
						cjms: state?.selectedFilterOptions?.cjms,
						personas: selectedItems,
						date: state?.selectedFilterOptions?.date,
					});

					const { touchpoints, metrics } = getInteractionsRelatedItems({
						interactions: [...currentPersonaInteractions, ...currentContactInteractions],
						allTouchpoints: state?.originalData?.touchpoints,
						allMetrics: state?.originalData?.metrics,
					});

					return {
						...state,
						allFilterOptions: { ...state?.allFilterOptions, touchpoints, metrics },
						selectedFilterOptions: {
							...state?.selectedFilterOptions,
							personas: selectedItems,
							touchpoints,
							metrics,
						},
						currentData: {
							personaInteractions: currentPersonaInteractions,
							contactInteractions: currentContactInteractions,
						},
						loading: false,
					};
				}
				case "touchpoints": {
					const { currentPersonaInteractions, currentContactInteractions } = getFilteredInteractions({
						personaInteractions: state?.originalData?.personaInteractions,
						contactInteractions: state?.originalData?.contactInteractions,
						cjms: state?.selectedFilterOptions?.cjms,
						personas: state?.selectedFilterOptions?.personas,
						touchpoints: selectedItems,
						metrics: state?.selectedFilterOptions?.metrics,
						date: state?.selectedFilterOptions?.date,
					});
					return {
						...state,
						selectedFilterOptions: { ...state.selectedFilterOptions, touchpoints: selectedItems },
						currentData: {
							personaInteractions: currentPersonaInteractions,
							contactInteractions: currentContactInteractions,
						},
					};
				}
				case "metrics": {
					const { currentPersonaInteractions, currentContactInteractions } = getFilteredInteractions({
						personaInteractions: state?.originalData?.personaInteractions,
						contactInteractions: state?.originalData?.contactInteractions,
						cjms: state?.selectedFilterOptions?.cjms,
						personas: state?.selectedFilterOptions?.personas,
						touchpoints: state?.selectedFilterOptions?.touchpoints,
						metrics: selectedItems,
						date: state?.selectedFilterOptions?.date,
					});
					return {
						...state,
						selectedFilterOptions: { ...state.selectedFilterOptions, metrics: selectedItems },
						currentData: {
							personaInteractions: currentPersonaInteractions,
							contactInteractions: currentContactInteractions,
						},
					};
				}
				case "date": {
					const { currentPersonaInteractions, currentContactInteractions } = getFilteredInteractions({
						personaInteractions: state?.originalData?.personaInteractions,
						contactInteractions: state?.originalData?.contactInteractions,
						cjms: state?.selectedFilterOptions?.cjms,
						personas: state?.selectedFilterOptions?.personas,
						touchpoints: state?.originalData?.touchpoints,
						metrics: state?.originalData?.metrics,
						date: selectedItems,
					});
					
                    const { touchpoints, metrics } = getInteractionsRelatedItems({
						interactions: [...currentPersonaInteractions, ...currentContactInteractions],
						allTouchpoints: state?.originalData?.touchpoints,
						allMetrics: state?.originalData?.metrics,
                    });
                    
					return {
                        ...state,
                        allFilterOptions: { ...state?.allFilterOptions, touchpoints, metrics },
						selectedFilterOptions: { ...state.selectedFilterOptions, touchpoints, metrics, date: selectedItems },
						currentData: {
							personaInteractions: currentPersonaInteractions,
							contactInteractions: currentContactInteractions,
						},
					};
				}
				default:
					return state;
			}
		}
		case success("GET_ANALYTICS_FILTERS"): {
			return {
				...state,
				savedAnalyticFilters: responseData,
			};
		}

		default:
			return state;
	}
};
export default vocAnalysisReducer;
